import { Injectable } from '@angular/core';
import { InStoreTextDetection, parseInStoreText } from '../shared/in-store-parser';

// Champs du formulaire « Poster » pré-remplis depuis les photos d'une offre en magasin
export interface InStoreDetection extends InStoreTextDetection {
  brand: string;
  category: string;
  // Photo du produit trouvée par son code-barres (Open Food Facts…), '' sinon
  productPictureUrl: string;
  barcode: string;
}

export type ScanProgress = (step: 'barcode' | 'text', progress: number) => void;

// Plus grand côté des photos envoyées : lisible pour la modération et l'OCR, bien sous la limite de 5 Mo de l'API
const MAX_PHOTO_SIDE = 1600;
const PHOTO_QUALITY = 0.85;
const LOOKUP_TIMEOUT_MS = 6000;
const BARCODE_FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e'];

// Bases ouvertes interrogées par code-barres, dans l'ordre, avec la catégorie PromoDeep de leurs produits
const PRODUCT_DATABASES: { host: string; category: string }[] = [
  { host: 'https://world.openfoodfacts.org', category: 'grocery' },
  { host: 'https://world.openbeautyfacts.org', category: 'cosmetic' },
  { host: 'https://world.openpetfoodfacts.org', category: 'animal' },
  { host: 'https://world.openproductsfacts.org', category: '' },
];

interface OpenFactsProduct {
  product_name?: string;
  product_name_fr?: string;
  brands?: string;
  quantity?: string;
  image_front_url?: string;
}

// Analyse des photos d'une offre en magasin, entièrement dans le navigateur (aucune photo envoyée à un tiers) :
// code-barres (BarcodeDetector, polyfill zxing sinon) puis fiche produit des bases Open Facts, et texte de
// l'étiquette (Tesseract.js, français) pour le prix, la remise, les dates et l'enseigne. Les bibliothèques
// et leurs fichiers (wasm, langue) ne sont téléchargés qu'à la première analyse.
@Injectable({
  providedIn: 'root'
})
export class InStoreScanService {

  private worker: Promise<any> | null = null;
  private progress: ScanProgress | null = null;

  // Photo redimensionnée et réencodée en JPEG : retire les métadonnées EXIF (position GPS du téléphone comprise)
  async preparePhoto(file: File): Promise<File> {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_PHOTO_SIDE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', PHOTO_QUALITY));
    if (!blob) {
      throw new Error('encode');
    }
    return new File([blob], (file.name || 'photo').replace(/\.[^.]*$/, '') + '.jpg', { type: 'image/jpeg' });
  }

  async scan(photos: File[], onProgress?: ScanProgress): Promise<InStoreDetection> {
    this.progress = onProgress || null;
    onProgress?.('barcode', 0);
    const barcode = await this.findBarcode(photos);
    const product = barcode ? await this.lookupBarcode(barcode) : null;

    onProgress?.('text', 0);
    const texts: string[] = [];
    for (const [index, photo] of photos.entries()) {
      texts.push(await this.readText(photo));
      onProgress?.('text', (index + 1) / photos.length);
    }
    this.progress = null;
    const detection = parseInStoreText(texts.join('\n'));
    return {
      ...detection,
      // Nom de la fiche produit, plus fiable que la ligne d'étiquette lue par l'OCR
      title: product?.title || detection.title,
      brand: product?.brand || '',
      category: product?.category || '',
      productPictureUrl: product?.pictureUrl || '',
      barcode,
    };
  }

  // Libère le moteur OCR (mémoire) en quittant la page
  release(): void {
    const worker = this.worker;
    this.worker = null;
    worker?.then(w => w.terminate()).catch(() => undefined);
  }

  private async readText(photo: File): Promise<string> {
    try {
      const worker = await this.getWorker();
      const { data } = await worker.recognize(photo);
      return data?.text || '';
    } catch {
      return '';
    }
  }

  private getWorker(): Promise<any> {
    if (!this.worker) {
      this.worker = import('tesseract.js').then(({ createWorker }) => createWorker('fra', 1, {
        logger: (message: { status: string; progress: number }) => {
          if (message.status === 'recognizing text') {
            this.progress?.('text', message.progress);
          }
        },
      }));
      this.worker.catch(() => this.worker = null);
    }
    return this.worker;
  }

  private async findBarcode(photos: File[]): Promise<string> {
    try {
      const Detector = 'BarcodeDetector' in window
        ? (window as any).BarcodeDetector
        : (await import('barcode-detector/ponyfill')).BarcodeDetector;
      const detector = new Detector({ formats: BARCODE_FORMATS });
      for (const photo of photos) {
        const bitmap = await createImageBitmap(photo);
        try {
          const codes: { rawValue: string }[] = await detector.detect(bitmap);
          const code = codes.map(c => (c.rawValue || '').trim()).find(value => /^\d{8,14}$/.test(value));
          if (code) {
            return code;
          }
        } finally {
          bitmap.close();
        }
      }
    } catch {
      // Lecteur indisponible : analyse du texte seulement
    }
    return '';
  }

  private async lookupBarcode(barcode: string): Promise<{ title: string; brand: string; category: string; pictureUrl: string } | null> {
    for (const database of PRODUCT_DATABASES) {
      const product = await this.fetchProduct(database.host, barcode);
      const name = (product?.product_name_fr || product?.product_name || '').trim();
      if (!product || !name) {
        continue;
      }
      const brand = (product.brands || '').split(',')[0].trim();
      // « 400 g e » / « 400 g ℮ » : signe de quantité estimée retiré
      const quantity = (product.quantity || '').replace(/\s*(℮|\be)$/i, '').trim();
      const title = [name, brand && !name.toLowerCase().includes(brand.toLowerCase()) ? brand : '', quantity]
        .filter(Boolean).join(' ').slice(0, 200);
      return { title, brand, category: database.category, pictureUrl: product.image_front_url || '' };
    }
    return null;
  }

  private async fetchProduct(host: string, barcode: string): Promise<OpenFactsProduct | null> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), LOOKUP_TIMEOUT_MS);
    try {
      const response = await fetch(`${host}/api/v2/product/${barcode}.json?fields=product_name,product_name_fr,brands,quantity,image_front_url`,
        { signal: controller.signal });
      if (!response.ok) {
        return null;
      }
      const body = await response.json();
      return body?.status === 1 ? body.product : null;
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }
}
